import { useState } from 'react';
import PropTypes from 'prop-types';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

function TransferPanel({
  title,
  items,
  checkedIds,
  onToggle,
  searchable = false,
  action = null,
}) {
  const [search, setSearch] = useState('');
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleItems = normalizedSearch
    ? items.filter(item => item.searchText?.toLocaleLowerCase().includes(normalizedSearch))
    : items;

  return (
    <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2">
            {title} ({items.length})
          </Typography>
          {action}
        </Box>
        {searchable && (
          <TextField
            fullWidth
            size="small"
            placeholder="Search by first or last name"
            value={search}
            onChange={event => setSearch(event.target.value)}
            sx={{ mt: 1.5 }}
          />
        )}
      </Box>
      <List dense disablePadding sx={{ height: 360, overflow: 'auto' }}>
        {visibleItems.length === 0 ? (
          <ListItem sx={{ py: 4, justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {normalizedSearch ? 'No matching players' : 'No players available'}
            </Typography>
          </ListItem>
        ) : (
          visibleItems.map(item => {
            const checked = checkedIds.includes(item.id);
            return (
              <ListItem key={item.id} disablePadding>
                <ListItemButton onClick={() => onToggle(item.id)}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText primary={item.label} secondary={item.secondaryLabel ?? null} />
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>
    </Paper>
  );
}

TransferPanel.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      secondaryLabel: PropTypes.string,
      searchText: PropTypes.string,
    })
  ).isRequired,
  checkedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  searchable: PropTypes.bool,
  action: PropTypes.node,
};

export default function PlayerTransferList({
  sourceItems,
  selectedItems,
  onChange,
  onReloadSource,
  sourceReloading = false,
}) {
  const [checkedIds, setCheckedIds] = useState([]);

  const toggleChecked = id => {
    setCheckedIds(current =>
      current.includes(id) ? current.filter(checkedId => checkedId !== id) : [...current, id]
    );
  };

  const moveRight = () => {
    const movingIds = new Set(checkedIds);
    const movingItems = sourceItems.filter(item => movingIds.has(item.id));
    onChange({
      sourceItems: sourceItems.filter(item => !movingIds.has(item.id)),
      selectedItems: [...selectedItems, ...movingItems],
    });
    setCheckedIds(current => current.filter(id => !movingIds.has(id)));
  };

  const moveLeft = () => {
    const movingIds = new Set(checkedIds);
    const movingItems = selectedItems.filter(item => movingIds.has(item.id));
    onChange({
      sourceItems: [...sourceItems, ...movingItems],
      selectedItems: selectedItems.filter(item => !movingIds.has(item.id)),
    });
    setCheckedIds(current => current.filter(id => !movingIds.has(id)));
  };

  const clearSelected = () => {
    onChange({
      sourceItems: [...sourceItems, ...selectedItems],
      selectedItems: [],
    });
    setCheckedIds([]);
  };

  const canMoveRight = sourceItems.some(item => checkedIds.includes(item.id));
  const canMoveLeft = selectedItems.some(item => checkedIds.includes(item.id));

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
      <TransferPanel
        title="Players from Paul's API"
        items={sourceItems}
        checkedIds={checkedIds}
        onToggle={toggleChecked}
        searchable
        action={
          <Tooltip title="Reload players from Paul's API">
            <span>
              <IconButton
                size="small"
                aria-label="Reload players from Paul's API"
                disabled={sourceReloading}
                onClick={onReloadSource}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <Stack
        direction={{ xs: 'row', md: 'column' }}
        spacing={1}
        justifyContent="center"
        alignItems="center"
      >
        <Button
          variant="outlined"
          size="small"
          aria-label="Move selected players to Players to Sync"
          disabled={!canMoveRight}
          onClick={moveRight}
        >
          <ArrowForwardIcon fontSize="small" />
        </Button>
        <Button
          variant="outlined"
          size="small"
          aria-label="Move selected players back to Paul's API players"
          disabled={!canMoveLeft}
          onClick={moveLeft}
        >
          <ArrowBackIcon fontSize="small" />
        </Button>
      </Stack>

      <TransferPanel
        title="Players to Sync"
        items={selectedItems}
        checkedIds={checkedIds}
        onToggle={toggleChecked}
        action={
          <Tooltip title="Clear players to sync">
            <span>
              <IconButton
                size="small"
                aria-label="Clear players to sync"
                disabled={selectedItems.length === 0}
                onClick={clearSelected}
              >
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />
    </Stack>
  );
}

PlayerTransferList.propTypes = {
  sourceItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      secondaryLabel: PropTypes.string,
    })
  ).isRequired,
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      secondaryLabel: PropTypes.string,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onReloadSource: PropTypes.func.isRequired,
  sourceReloading: PropTypes.bool,
};
